// Procedural texture baking + swap-safe registry. (TECHNICAL_DESIGN.md §5.)
// Every sprite is requested by NAME via tex(); a real CC0 atlas can later register the same
// names and win without any game-code change.
import { Application, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { CHUNK, TILE } from './const';
import type { Chunk, NodeKind, TileId } from './types';

const TILE_COLORS: Record<TileId, number> = {
  0: 0x2d4a6b, // water
  1: 0xd9c98f, // beach sand
  2: 0x5a8b3a, // meadow grass
  3: 0x3f6b2c, // forest dark grass
  4: 0x7d7566, // highlands rock
};

const registry = new Map<string, Texture>();
let app: Application | null = null;

export function initTextures(a: Application): void {
  app = a;
  bakeSprites();
}

/** Look up a texture by name (procedural now, atlas-swappable later). */
export function tex(name: string): Texture {
  return registry.get(name) ?? Texture.WHITE;
}

function bake(name: string, g: Graphics): void {
  if (!app) return;
  const t = app.renderer.generateTexture(g);
  registry.set(name, t);
  g.destroy();
}

/** Small emoji/shape props baked once at boot. */
// Light character palette — small fixed swatch sets keep customization deterministic + cheap.
export const PALETTE = {
  body: [0xf7dcae, 0xe0b088, 0xc68642, 0x8d5524], // skin tones
  hair: [0x7a4a24, 0x241a12, 0xc9a24b, 0x9a3a2a], // hair/cap
  outfit: [0xf07a2a, 0x3f78c0, 0x3fa060, 0xa04a8a], // tunic
};
function pick(arr: number[], i: number): number { return arr[((i % arr.length) + arr.length) % arr.length]; }

/** (Re)bake the two player textures from a palette. Parametric — never couples the shared
 *  registry to live mutable state; the player texture is inherently per-save. */
export function bakePlayer(pal: { body: number; hair: number; outfit: number } = { body: 0, hair: 0, outfit: 0 }): void {
  const skin = pick(PALETTE.body, pal.body), hair = pick(PALETTE.hair, pal.hair), outfit = pick(PALETTE.outfit, pal.outfit);
  // Both player textures share the 44×40 frame so switching weapons never shifts the sprite.
  const drawBody = (g: Graphics) => {
    g.rect(0, 0, 44, 40).fill({ color: 0x000000, alpha: 0 }); // invisible shared frame
    g.ellipse(17, 33, 11, 4).fill({ color: 0x000000, alpha: 0.28 }); // ground shadow
    g.roundRect(9, 15, 16, 17, 7).fill(outfit); // tunic
    g.roundRect(9, 15, 16, 17, 7).stroke({ width: 2.5, color: 0x2a1608 });
    g.circle(17, 10, 7.5).fill(skin); // head
    g.circle(17, 10, 7.5).stroke({ width: 2.5, color: 0x2a1608 });
    // hair: a rounded cap over the top of the head (not a flat band)
    g.arc(17, 9.5, 7.9, Math.PI, Math.PI * 2).fill(hair);
  };
  {
    const g = new Graphics();
    drawBody(g);
    bake('player', g);
  }
  {
    const g = new Graphics();
    drawBody(g);
    // the bow: a clear C-bow held out on the right, with string and a nocked arrow
    g.arc(30, 20, 11, -1.5, 1.5).stroke({ width: 3, color: 0x9a5a24 });
    const sx = 30 + Math.cos(-1.5) * 11, sy = 20 + Math.sin(-1.5) * 11;
    const ex = 30 + Math.cos(1.5) * 11, ey = 20 + Math.sin(1.5) * 11;
    g.moveTo(sx, sy).lineTo(ex, ey).stroke({ width: 1.2, color: 0xf0e4c4 }); // string
    g.moveTo(18, 20).lineTo(34, 20).stroke({ width: 1.6, color: 0xd8c060 }); // arrow shaft
    g.moveTo(34, 20).lineTo(31, 18).lineTo(31, 22).closePath().fill(0xcfc0a0); // arrowhead
    bake('player_bow', g);
  }
}

function bakeSprites(): void {
  bakePlayer(); // default palette; game re-bakes from the save on start
  // mobs
  bakeMob('slime', 0x6ab04a, 0x4a8a2f);
  bakeMob('wolf', 0x8a8a92, 0x5a5a62);
  bakeMob('skeleton', 0xe8e4d8, 0x9a9488);
  bakeMob('boar', 0x8a5a34, 0x5a3a1e);        // sturdy brown day wildlife
  bakeMob('direwolf', 0x4a4a58, 0x26262f);    // dark night pack hunter
  bakeMob('wraith', 0x8a6ab0, 0x3a2a5a);      // ghostly purple night horror
  bakeMob('wisp', 0xa8f0ec, 0x5fd8d4);        // rare glowing night light
  bakeMob('golden_slime', 0xf0d040, 0xb08a10); // rare gold jackpot
  bakeMob('bone_warden', 0xe8ddc8, 0x9a3a3a); // fallback (bosses use the dedicated textures below)
  // the boss ladder, each with its own model: bone → armored bone → violet king
  bakeAcolyte();   // 'boss_acolyte' — floors 3 & 8: a small hooded skeletal cultist
  bakeBoneWarden(); // 'boss_warden' — floor 5: a bulky horned skeleton in armour plates
  bakeFinalBoss(); // 'dungeon_boss' — floor 10 Hollow King: a crowned violet horror
  // drops
  {
    const g = new Graphics();
    g.circle(7, 7, 6).fill(0xf0d040).stroke({ width: 1.5, color: 0x8a6a10 });
    bake('drop_gold', g);
  }
  {
    const g = new Graphics();
    g.circle(7, 7, 6).fill(0xc98a6a).stroke({ width: 1.5, color: 0x5a3a2a });
    bake('drop_item', g);
  }
  // swing arc + arrow
  {
    const g = new Graphics();
    g.moveTo(0, 0).arc(0, 0, 46, -0.9, 0.9).lineTo(0, 0).fill({ color: 0xffffff, alpha: 0.35 });
    bake('swing', g);
  }
  {
    const g = new Graphics();
    g.roundRect(0, 3, 16, 3, 1.5).fill(0xd8c060).stroke({ width: 1, color: 0x6a5010 });
    bake('arrow', g);
  }
  {
    // a standalone bow that points RIGHT (+x); the game rotates it to the player's facing so the
    // bow aims where you walk in any direction. Drawn ~symmetric around the 22×22 centre.
    const g = new Graphics();
    g.rect(0, 0, 22, 22).fill({ color: 0x000000, alpha: 0 }); // fixed frame → stable centre
    g.arc(9, 11, 8, -1.5, 1.5).stroke({ width: 2.5, color: 0x9a5a24 }); // C-bow opening right
    const sx = 9 + Math.cos(-1.5) * 8, sy = 11 + Math.sin(-1.5) * 8;
    const ex = 9 + Math.cos(1.5) * 8, ey = 11 + Math.sin(1.5) * 8;
    g.moveTo(sx, sy).lineTo(ex, ey).stroke({ width: 1.1, color: 0xf0e4c4 }); // string
    g.moveTo(5, 11).lineTo(20, 11).stroke({ width: 1.5, color: 0xd8c060 }); // nocked arrow shaft
    g.moveTo(20, 11).lineTo(17, 9).lineTo(17, 13).closePath().fill(0xcfc0a0); // arrowhead
    bake('bow_held', g);
  }
  {
    // a held sword that points RIGHT (+x); rotated to the player's facing like the bow
    const g = new Graphics();
    g.rect(0, 0, 24, 24).fill({ color: 0x000000, alpha: 0 }); // fixed frame → stable centre
    g.moveTo(11, 12).lineTo(22, 9.5).lineTo(23.5, 12).lineTo(22, 14.5).closePath().fill(0xdcdce6).stroke({ width: 1, color: 0x8a8a99 }); // blade
    g.roundRect(9, 7.5, 2.6, 9, 1).fill(0x9a7a3a).stroke({ width: 1, color: 0x5a4520 }); // crossguard
    g.roundRect(4.5, 10.6, 5, 2.8, 1).fill(0x6a4a24); // grip
    g.circle(4.5, 12, 1.8).fill(0xb08a4a); // pommel
    bake('sword_held', g);
  }
  bakeNode('tree', 0x2f5d1f, 0x6b4a2a, 18);
  bakeNode('bush', 0x4a7a2f, 0x7a3b6a, 12);
  bakeNode('stone', 0x8a8478, 0x6a6458, 14);
  bakeNode('iron', 0x9a8a7a, 0xb08a5a, 15);
  bakeNode('gold', 0xb0a060, 0xf0d040, 15);
  bakeNode('entrance', 0x2a2230, 0x14101a, 16);
  bakeNode('totem', 0x4a3a5a, 0x2a2038, 16);
  // placeables
  {
    const g = new Graphics();
    g.ellipse(14, 26, 11, 4).fill({ color: 0x000000, alpha: 0.25 });
    // logs
    g.roundRect(5, 22, 18, 5, 2).fill(0x5a3a1e).stroke({ width: 1.5, color: 0x2a1808 });
    g.roundRect(8, 24, 12, 4, 2).fill(0x6a4a24);
    // flame
    g.moveTo(14, 24).lineTo(9, 14).lineTo(14, 6).lineTo(19, 14).closePath().fill(0xf07a1a);
    g.moveTo(14, 22).lineTo(11, 15).lineTo(14, 10).lineTo(17, 15).closePath().fill(0xf7d020);
    bake('campfire', g);
  }
  {
    const g = new Graphics();
    g.roundRect(11, 12, 4, 18, 2).fill(0x6a4a24).stroke({ width: 1, color: 0x2a1808 }); // pole
    g.moveTo(13, 12).lineTo(9, 5).lineTo(13, 0).lineTo(17, 5).closePath().fill(0xf7c020); // flame
    bake('torch', g);
  }
  {
    const g = new Graphics();
    g.roundRect(2, 6, 28, 22, 3).fill(0x6a4a26).stroke({ width: 2, color: 0x3a2812 });
    for (let i = 1; i < 4; i++) g.rect(2 + i * 7, 6, 1.5, 22).fill(0x3a2812); // planks
    bake('wooden_wall', g);
  }
  {
    // home portal: a stone arch around a swirling teal rift
    const g = new Graphics();
    g.ellipse(15, 27, 11, 4).fill({ color: 0x000000, alpha: 0.25 }); // shadow
    g.ellipse(15, 15, 12, 15).fill(0x5a5060).stroke({ width: 2.5, color: 0x2a2434 }); // stone arch
    g.ellipse(15, 15, 8, 11).fill(0x1a3a44); // rift
    g.ellipse(15, 15, 5, 7).fill(0x2fb0b8);
    g.ellipse(15, 13, 2.5, 4).fill(0xa8f0ec); // inner glow
    bake('home_portal', g);
  }
  // warm radial glow for fires (additive)
  {
    const g = new Graphics();
    for (let i = 6; i >= 1; i--) g.circle(64, 64, i * 9).fill({ color: 0xffcc66, alpha: 0.06 });
    bake('glow', g);
  }
  // dungeon props
  {
    const g = new Graphics();
    g.roundRect(3, 10, 22, 14, 3).fill(0x7a5a2a).stroke({ width: 2, color: 0x2a1808 });
    g.roundRect(3, 8, 22, 6, 3).fill(0x9a7a3a).stroke({ width: 2, color: 0x2a1808 }); // lid
    g.rect(12, 8, 4, 16).fill(0xd8b040); // latch
    bake('chest', g);
  }
  {
    const g = new Graphics();
    g.rect(2, 2, 28, 28).fill(0x14101c).stroke({ width: 2, color: 0x3a3450 });
    for (let i = 0; i < 4; i++) g.rect(2 + i * 3, 2 + i * 6, 28 - i * 6, 5).fill(0x2a2440); // descending steps
    bake('stairs', g);
  }
  {
    // spike trap: a dark 32×32 tile with jagged metal spikes (clearly a "don't step here")
    const g = new Graphics();
    g.rect(0, 0, 32, 32).fill(0x241820).stroke({ width: 1.5, color: 0x120c14 });
    for (let i = 0; i < 4; i++) {
      const x = 4 + i * 7;
      g.moveTo(x, 28).lineTo(x + 3, 6).lineTo(x + 6, 28).closePath().fill(0xb8b0c0).stroke({ width: 1, color: 0x5a5568 });
    }
    g.moveTo(0, 28).lineTo(32, 28).stroke({ width: 2, color: 0x3a2a2a }); // base rail
    bake('spike', g);
  }
  {
    const g = new Graphics();
    g.star(9, 9, 5, 8, 3.5).fill(0x9a6adf).stroke({ width: 1.5, color: 0xe0c0ff });
    bake('relic', g);
  }
}

/** Bake a whole dungeon floor (walls + floor tiles) into ONE texture. */
export function bakeDungeonSprite(size: number, tiles: Uint8Array): Sprite {
  const g = new Graphics();
  g.rect(0, 0, size * TILE, size * TILE).fill(0x161320); // wall background
  for (let ty = 0; ty < size; ty++) {
    for (let tx = 0; tx < size; tx++) {
      if (tiles[ty * size + tx] === 1) {
        g.rect(tx * TILE, ty * TILE, TILE, TILE).fill(0x413a5e); // lighter floor so the lit area reads
        if (((tx + ty) & 1) === 0) g.rect(tx * TILE, ty * TILE, TILE, TILE).fill({ color: 0x000000, alpha: 0.10 });
      }
    }
  }
  const t = app!.renderer.generateTexture({ target: g, resolution: 1 });
  g.destroy();
  return new Sprite(t);
}

function bakeMob(name: string, main: number, accent: number): void {
  const g = new Graphics();
  g.ellipse(13, 24, 9, 3).fill({ color: 0x000000, alpha: 0.25 }); // shadow
  g.roundRect(4, 8, 18, 16, 7).fill(main);
  g.roundRect(4, 8, 18, 16, 7).stroke({ width: 2, color: 0x1a1410 });
  g.circle(10, 13, 2).fill(0x1a1410); // eyes
  g.circle(16, 13, 2).fill(0x1a1410);
  if (name === 'wolf') { g.moveTo(4, 8).lineTo(8, 2).lineTo(10, 8).fill(main); g.moveTo(22, 8).lineTo(18, 2).lineTo(16, 8).fill(main); } // ears
  bake(name, g);
}

/** Warden Acolyte (floors 3 & 8) — a small hooded skeletal cultist: a tattered dark robe with a
 *  bone skull peeking from the hood and dim red sockets. The "lesser" rung of the boss ladder. */
function bakeAcolyte(): void {
  const g = new Graphics();
  g.rect(0, 0, 26, 28).fill({ color: 0x000000, alpha: 0 }); // fixed frame → stable bounds/anchor
  g.ellipse(13, 25, 9, 3).fill({ color: 0x000000, alpha: 0.28 }); // ground shadow
  // tattered robe (a lean, torn silhouette)
  g.moveTo(5, 24).lineTo(7, 12).lineTo(19, 12).lineTo(21, 24)
    .lineTo(18, 22).lineTo(15, 25).lineTo(13, 22).lineTo(11, 25).lineTo(8, 22).closePath()
    .fill(0x3a3040).stroke({ width: 1.5, color: 0x1a141f });
  g.roundRect(7, 6, 12, 11, 5).fill(0x4a4052).stroke({ width: 1.5, color: 0x201a28 }); // hood
  g.circle(13, 12, 4).fill(0xe8ddc8); // bone skull peeking from the hood
  g.circle(11.5, 12, 1.2).fill(0x8a2a2a); // dim red sockets
  g.circle(14.5, 12, 1.2).fill(0x8a2a2a);
  bake('boss_acolyte', g);
}

/** Bone Warden (floor 5) — a bulky horned skeleton: a ribcage torso, dark-red armour plates on the
 *  shoulders, a horned skull with glowing red sockets. The mid-run wall, clearly bigger than the
 *  acolyte and clearly bone (setting up the violet King as the escalation). */
function bakeBoneWarden(): void {
  const g = new Graphics();
  g.rect(0, 0, 26, 28).fill({ color: 0x000000, alpha: 0 }); // fixed frame → stable bounds/anchor
  g.ellipse(13, 26, 11, 3.5).fill({ color: 0x000000, alpha: 0.3 }); // ground shadow
  g.roundRect(5, 12, 16, 12, 4).fill(0xe8ddc8).stroke({ width: 2, color: 0x6a5a3a }); // ribcage torso
  for (let i = 0; i < 3; i++) g.moveTo(7, 15 + i * 3).lineTo(19, 15 + i * 3).stroke({ width: 1.4, color: 0x9a8a68 }); // ribs
  g.roundRect(1, 11, 6, 5, 2).fill(0x9a3a3a).stroke({ width: 1.5, color: 0x5a1e1e }); // armour plates
  g.roundRect(19, 11, 6, 5, 2).fill(0x9a3a3a).stroke({ width: 1.5, color: 0x5a1e1e });
  g.circle(13, 8, 6).fill(0xf0e8d4).stroke({ width: 2, color: 0x6a5a3a }); // skull
  g.moveTo(8, 5).lineTo(4, 0).lineTo(10, 4).closePath().fill(0xd8c8a8); // horns
  g.moveTo(18, 5).lineTo(22, 0).lineTo(16, 4).closePath().fill(0xd8c8a8);
  g.circle(11, 8, 1.7).fill(0xd23a2a); // glowing red sockets
  g.circle(15, 8, 1.7).fill(0xd23a2a);
  g.rect(10, 11, 6, 2).fill(0xf0e8d4); // jaw/teeth
  bake('boss_warden', g);
}

/** The final Dungeon Boss ("The Hollow King") — a distinct crowned, hooded violet horror with a
 *  void face and glowing eyes, drawn in the same ~26×28 footprint as a mob so the game's boss
 *  anchor/scale land it like the others. Violet ties it to the relic/legendary it drops. */
function bakeFinalBoss(): void {
  const g = new Graphics();
  g.rect(0, 0, 26, 28).fill({ color: 0x000000, alpha: 0 }); // fixed frame → stable bounds/anchor
  g.ellipse(13, 26, 11, 3.5).fill({ color: 0x000000, alpha: 0.3 }); // ground shadow
  // ragged robe (a widening silhouette with a torn hem)
  g.moveTo(2, 25).lineTo(5, 11).lineTo(21, 11).lineTo(24, 25)
    .lineTo(20, 23).lineTo(17, 26).lineTo(13, 23).lineTo(9, 26).lineTo(6, 23).closePath()
    .fill(0x2a1c3e).stroke({ width: 2, color: 0x120a1e });
  // hooded head
  g.roundRect(6, 8, 14, 13, 6).fill(0x4a2f6e).stroke({ width: 2, color: 0x1a1028 });
  g.ellipse(13, 14, 4.5, 5.5).fill(0x0c0814); // void face
  g.circle(11, 14, 1.7).fill(0xffb020); // glowing eyes
  g.circle(15, 14, 1.7).fill(0xffb020);
  // jagged crown of three spikes
  g.moveTo(6, 9).lineTo(4, 2).lineTo(9, 8).closePath().fill(0x7a4aa0);
  g.moveTo(20, 9).lineTo(22, 2).lineTo(17, 8).closePath().fill(0x7a4aa0);
  g.moveTo(10, 7).lineTo(13, 0.5).lineTo(16, 7).closePath().fill(0x9a5ac0);
  bake('dungeon_boss', g);
}

function bakeNode(name: string, main: number, accent: number, r: number): void {
  const g = new Graphics();
  g.roundRect(r - 3, r, 6, r, 3).fill(0x5a3a1e); // trunk-ish base
  g.circle(r, r - 2, r * 0.8).fill(main);
  g.circle(r + r * 0.3, r - r * 0.3, r * 0.3).fill(accent);
  g.circle(r, r - 2, r * 0.8).stroke({ width: 1.5, color: 0x1a1208 });
  bake(name, g);
}

/**
 * Bake a chunk's ground into ONE texture (resolution 1 — never DPR; §5 memory rule) and
 * return a positioned sprite. 24×24 tiles → 1 sprite.
 */
export function bakeChunkSprite(chunk: Chunk): Sprite {
  const c = new Container();
  for (let ly = 0; ly < CHUNK; ly++) {
    for (let lx = 0; lx < CHUNK; lx++) {
      const t = chunk.tiles[ly * CHUNK + lx] as TileId;
      const g = new Graphics();
      g.rect(lx * TILE, ly * TILE, TILE, TILE).fill(TILE_COLORS[t]);
      // very subtle checker for a sense of motion without a distracting grid
      if (((lx + ly) & 1) === 0) g.rect(lx * TILE, ly * TILE, TILE, TILE).fill({ color: 0xffffff, alpha: 0.015 });
      c.addChild(g);
    }
  }
  const t = app!.renderer.generateTexture({ target: c, resolution: 1 });
  c.destroy({ children: true });
  const sp = new Sprite(t);
  sp.x = chunk.cx * CHUNK * TILE;
  sp.y = chunk.cy * CHUNK * TILE;
  return sp;
}

export function nodeTextureName(kind: NodeKind): string {
  return kind;
}
